import { actions, containers } from '@ecosystem/newron-design-system';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { actionCreatorsSideFilters } from '@state/Index';
import { clearAllFilters, filtersToRender, getFilterResponseConfigByPageType } from '@utils/FilterSettings';
import { onFilterTrayApply, onFilterTrayClear } from '@utils/filterTrayUtils/FilterTrayUtils';
import { isBaseFiltersWithAtLeastOneOtherFilterApplied } from '@utils/FilterUtils';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { FilterKeys, FilterObject, PageName, PageType, PageTypeAndName } from '@utils/pageConfig/Types';
import { isSelectedDiffWithApplied } from '@utils/validator/CommonValidator';
import FilterComponent from '../filter-component/FilterComponent';
import { useAnalytics } from '@ecosystem/newron-client-lib';
import { IRootState } from '@state/reducers/Reducers';
import { getEffectiveProductTypeCatch } from '../filters-tray/FiltersTray';
import { cloneDeep } from 'lodash';

const ListFilters: React.FC = () => {
  const pagesConfig = usePageConfig();
  const currentPage = pagesConfig.pageType;

  const dispatch = useDispatch();
  const { updateSideFilters } = bindActionCreators(actionCreatorsSideFilters, dispatch);
  const retailerClientState = useSelector((state: any) => state.retailerClient);
  const { track } = useAnalytics();

  const subscribedProductTypes = useSelector(
    (state: IRootState) => state.subscribedProductTypes.subscribedProductTypes,
  );

  const effectiveProductType = getEffectiveProductTypeCatch(subscribedProductTypes, pagesConfig.pageName);

  let defaultFilters = getFilterResponseConfigByPageType(currentPage).response;

  const removeFilter = (filterKey: FilterKeys) => {
    let productSpecIdx = defaultFilters.findIndex((v) => v.mainTitle === 'Product Specification');

    defaultFilters[productSpecIdx].filters = defaultFilters[productSpecIdx].filters.filter(
      (v) => v.filterKey !== filterKey,
    );
  };

  // remove priceband if retailer overview or category leaderboard
  if (currentPage === PageType.RETAILER_OVERVIEW || currentPage === PageType.CATEGORY_LEADERBOARD) {
    removeFilter(FilterKeys.PRICEBAND);
  }
  if (pagesConfig.pageName === PageName.TRADING_ASSISTANT) {
    removeFilter(FilterKeys.BRAND);
  }

  const [filters, setFilters] = useState<FilterObject[]>(defaultFilters);

  const onApply = async () => {
    onFilterTrayApply(pagesConfig, updateSideFilters, retailerClientState, track, effectiveProductType);
  };

  const onClear = () => {
    onFilterTrayClear(pagesConfig);
  };

  /*
   * updates the filter options when pg selection is changed in pg overview
   */
  useEffect(() => {
    if (pagesConfig.selectedPgUpdated) {
      setFilters(getFilterResponseConfigByPageType(currentPage).response);
      pagesConfig.setSelectedPgUpdated(false);

      // if base filters are applied, has difference between selected and applied and not RO or CL page
      const pageTypeAndName: PageTypeAndName = pagesConfig.getPageTypeAndName();
      if (isSelectedDiffWithApplied(pagesConfig, FilterKeys.PRODUCT_GROUP)) {
        // DON'T SIMPLY EXCLUDE THE EXPLORE PAGE TO CLEAR FILTERS BECAUSE TO CLEAR THE SELECTED PRODUCT FEATURES IS NEEDED IF GOT CHANGES ON SELECTED PG
        if (
          isBaseFiltersWithAtLeastOneOtherFilterApplied(pageTypeAndName, pagesConfig.selectedFilters) &&
          ![PageType.RETAILER_OVERVIEW, PageType.CATEGORY_LEADERBOARD].includes(currentPage)
        ) {
          const fil = clearAllFilters(pagesConfig.selectedFilters, pageTypeAndName, false, true);
          pagesConfig.setSelectedFilters(fil);
          pagesConfig.setClearFilters(false);
        }
        pagesConfig.setApplyFilters(true);
        pagesConfig.setClearFilters(true);
      }
    }
  }, [pagesConfig.selectedPgUpdated]);

  /*
   * updates the filter options when list of PGs are updated
   */
  useEffect(() => {
    if (pagesConfig.pgListUpdated) {
      // only clear the filters if the country selection change. this check prevents reset during page navigation
      if (
        ![PageType.EXPLORE, PageType.OPPORTUNITY_REVIEW, PageType.TRADING_ASSISTANT].includes(pagesConfig.pageType) &&
        isSelectedDiffWithApplied(pagesConfig, FilterKeys.COUNTRY)
      ) {
        const fil = clearAllFilters(pagesConfig.selectedFilters, pagesConfig.getPageTypeAndName());
        pagesConfig.setSelectedFilters(fil);
      }

      pagesConfig.setPgListUpdated(false);
      setFilters(getFilterResponseConfigByPageType(currentPage).response);
    }
  }, [pagesConfig.pgListUpdated]);

  const toRender = (filterObjects: FilterObject[]) => {
    return (
      <>
        {filtersToRender(pagesConfig.pageName, filterObjects).map((component) => (
          <div key={component.mainTitle}>
            <FilterComponent filterObject={component} />
          </div>
        ))}
      </>
    );
  };

  /**
   * Renders the collapsible filters based on the provided filter objects.
   * @param filterObjects - An array of filter objects.
   * @returns An array of JSX elements representing the collapsible filters.
   *
   * -- To replace the existing one once side filter changes are finalized
   */
  const toRenderCollapsibleFilters = (filterObjects: FilterObject[]) => {
    const { Accordion } = containers;
    const filtersRenderArray = filtersToRender(pagesConfig.pageName, cloneDeep(filterObjects));

    return filtersRenderArray.map((component) => {
      return (
        <div key={component.mainTitle}>
          <Accordion flushAlign={true} key={component.mainTitle} hoverColor="bg-medium" iconRight={true}>
            <Accordion.Item key={component.mainTitle} title={component.mainTitle} data-testid="filter-accordion" isOpen>
              <div>
                <FilterComponent filterObject={component} />
              </div>
            </Accordion.Item>
          </Accordion>
        </div>
      );
    });
  };

  const { Button } = actions;
  const { FilterTray } = containers;

  const getFiltersUponPgUpdated = () => {
    if (pagesConfig.selectedPgUpdated) {
      return filters;
    }
    return defaultFilters;
  };

  return (
    <>
      <FilterTray.Content>
        {pagesConfig.isSideFiltersFeatureFlagEnabled
          ? toRenderCollapsibleFilters(getFiltersUponPgUpdated())
          : toRender(getFiltersUponPgUpdated())}
      </FilterTray.Content>
      <FilterTray.Footer>
        <div className="flex">
          <Button
            testId="cancel-filter"
            disabled={!pagesConfig.clearFilters}
            mode="secondary"
            stretch
            onClick={onClear}
          >
            Clear
          </Button>
          <Button type="submit" testId="apply-filter" disabled={!pagesConfig.applyFilters} stretch onClick={onApply}>
            Apply
          </Button>
        </div>
      </FilterTray.Footer>
    </>
  );
};

export default ListFilters;
